import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Camera, Calendar, Image, Users, ArrowRight, Check, 
  BookOpen, BarChart3, ShoppingCart, Zap, Shield, Clock,
  Mail, Phone, Globe, Instagram, Facebook, Twitter, Menu, X,
  HelpCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import axios from "axios";
import { API, useAuth } from "../App";
import SEO from "../components/SEO";

const HomePage = () => {
  const [adminContact, setAdminContact] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const fetchAdminContact = async () => {
      try {
        const response = await axios.get(`${API}/admin/public-contact`);
        setAdminContact(response.data);
      } catch (error) {
        console.error("Failed to fetch admin contact:", error);
      }
    };
    fetchAdminContact();
  }, []);

  const features = [
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "Smart Booking System",
      description: "Let clients book sessions directly with real-time availability. Manage requests, confirmations, and schedule all in one place."
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: "Calendar Management",
      description: "Set your availability, block off dates, and sync with your existing calendar. Never double-book again."
    },
    {
      icon: <Image className="w-8 h-8" />,
      title: "Gallery Delivery",
      description: "Create beautiful galleries for clients with private magic-link access. Organize, watermark, and deliver photos effortlessly."
    },
    {
      icon: <ShoppingCart className="w-8 h-8" />,
      title: "Photo Sales",
      description: "Sell photos directly from public event galleries. Set your prices, customers pay online, you get paid automatically."
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Business Analytics",
      description: "Track bookings, revenue, and client activity. Understand your business performance at a glance."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Custom Branding",
      description: "Your logo, your name, your brand. Clients see your professional identity, not ours."
    }
  ];

  const benefits = [
    "Unlimited photo uploads on Pro plans",
    "Custom branded client portals",
    "Automated email notifications",
    "Secure payment processing",
    "Mobile-friendly galleries",
    "No technical setup required"
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="360Lumina | Professional Photography Business Management Platform"
        description="360Lumina helps photographers streamline their business with booking management, client galleries, contracts, payments, and more. Start your free trial today."
        canonical="https://www.360lumina.com/"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "360Lumina",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "description": "Professional photography business management platform with booking, galleries, contracts, and payments.",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          }
        }}
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Camera className="w-8 h-8 text-primary" />
            <span className="text-2xl font-serif tracking-tight">360Lumina</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/features" className="nav-link" data-testid="nav-features">
              Features
            </Link>
            <Link to="/pricing" className="nav-link" data-testid="nav-pricing">
              Pricing
            </Link>
            <Link to="/galleries" className="nav-link" data-testid="nav-galleries">
              Demo Galleries
            </Link>
            <Link to="/help" className="nav-link" data-testid="nav-help">
              Help
            </Link>
            {user ? (
              <Link to="/dashboard">
                <Button className="btn-primary rounded-sm" data-testid="nav-dashboard">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button className="btn-secondary rounded-sm" data-testid="nav-login">
                  Login
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-muted-foreground hover:text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/5 bg-background/95 backdrop-blur-md">
            <div className="px-6 py-4 space-y-4">
              <Link 
                to="/features" 
                className="block text-lg text-muted-foreground hover:text-white transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Features
              </Link>
              <Link 
                to="/pricing" 
                className="block text-lg text-muted-foreground hover:text-white transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </Link>
              <Link 
                to="/galleries" 
                className="block text-lg text-muted-foreground hover:text-white transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Demo Galleries
              </Link>
              <Link 
                to="/help" 
                className="block text-lg text-muted-foreground hover:text-white transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Help
              </Link>
              {user ? (
                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="btn-primary rounded-sm w-full">
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="btn-secondary rounded-sm w-full">
                    Login
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Sales Focused */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1554080353-a576cf803bda?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
            alt="Professional photographer"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="hero-gradient absolute inset-0" />
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-6 max-w-5xl"
        >
          <p className="text-sm uppercase tracking-widest text-primary mb-4">
            For Professional Photographers
          </p>
          <h1 className="text-5xl md:text-7xl tracking-tight leading-none mb-6">
            Everything You Need to Run Your Photography Business
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Booking, galleries, sales, and client management — all in one beautiful platform. 
            Stop juggling multiple tools and focus on what you do best: capturing amazing moments.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/pricing">
              <Button className="btn-primary rounded-sm text-lg px-8 py-6" data-testid="hero-get-started">
                Get Started Free <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/galleries">
              <Button className="btn-secondary rounded-sm text-lg px-8 py-6" data-testid="hero-see-demo">
                See Demo Galleries
              </Button>
            </Link>
          </div>
          
          <p className="text-sm text-muted-foreground mt-6">
            No credit card required • Free Pro Trial Available
          </p>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1 h-3 bg-white/50 rounded-full mt-2"
            />
          </div>
        </motion.div>
      </section>

      {/* Features Grid */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-widest text-primary mb-4">Features</p>
            <h2 className="text-4xl md:text-5xl tracking-tight mb-4">
              Built for Photographers, by Photographers
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Every feature designed to save you time and help you grow your business
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-card border-border h-full card-hover">
                  <CardContent className="p-8">
                    <div className="text-primary mb-4">{feature.icon}</div>
                    <h3 className="text-2xl mb-3 font-serif">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 bg-card/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-sm uppercase tracking-widest text-primary mb-4">Why 360Lumina</p>
              <h2 className="text-4xl md:text-5xl tracking-tight mb-6">
                Focus on Photography, Not Admin Work
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                We handle the technical stuff so you can spend more time behind the camera and less time managing spreadsheets and emails.
              </p>
              
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-primary" />
                    </div>
                    <span>{benefit}</span>
                  </motion.li>
                ))}
              </ul>
              
              <Link to="/pricing" className="inline-block mt-8">
                <Button className="btn-primary rounded-sm">
                  View Pricing Plans
                </Button>
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1516035069371-29a1b244cc32?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Photographer at work"
                className="w-full rounded-sm"
              />
              <div className="absolute -bottom-6 -left-6 bg-card border border-border rounded-sm p-6 shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-green-400/20 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="font-semibold">Secure & Reliable</p>
                    <p className="text-sm text-muted-foreground">Your photos are safe with us</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-widest text-primary mb-4">How It Works</p>
            <h2 className="text-4xl md:text-5xl tracking-tight">
              Get Started in Minutes
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Create Your Account",
                description: "Sign up with Google and set up your profile with your branding and portfolio."
              },
              {
                step: "02",
                title: "Share Your Link",
                description: "Send your personalized booking page to clients. They can view availability and request sessions."
              },
              {
                step: "03",
                title: "Manage & Deliver",
                description: "Accept bookings, create galleries, and deliver photos. Get paid automatically for sales."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-6xl font-serif text-primary/30 mb-4">{item.step}</div>
                <h3 className="text-2xl mb-3 font-serif">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-primary/5 border-t border-b border-primary/20">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl tracking-tight mb-6">
              Ready to Streamline Your Photography Business?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join photographers who are saving hours every week with 360Lumina.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pricing">
                <Button className="btn-primary rounded-sm text-lg px-8 py-6">
                  Start Free Trial
                </Button>
              </Link>
              <Link to="/features">
                <Button className="btn-secondary rounded-sm text-lg px-8 py-6">
                  Explore Features
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      {adminContact && (
        <section className="py-24 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p className="text-sm uppercase tracking-widest text-primary mb-4">Contact</p>
              <h2 className="text-4xl md:text-5xl tracking-tight mb-4">
                Questions? Let&apos;s Talk
              </h2>
              <p className="text-lg text-muted-foreground">
                We&apos;re here to help you get the most out of 360Lumina
              </p>
            </motion.div>

            <Card className="bg-card border-border">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <h3 className="text-2xl font-serif mb-4">
                      {adminContact.business_name || adminContact.name || "360Lumina Support"}
                    </h3>
                    <div className="space-y-3">
                      {adminContact.email && (
                        <a 
                          href={`mailto:${adminContact.email}`}
                          className="flex items-center gap-3 text-muted-foreground hover:text-white transition-colors"
                        >
                          <Mail className="w-5 h-5" />
                          {adminContact.email}
                        </a>
                      )}
                      {adminContact.phone && (
                        <a 
                          href={`tel:${adminContact.phone}`}
                          className="flex items-center gap-3 text-muted-foreground hover:text-white transition-colors"
                        >
                          <Phone className="w-5 h-5" />
                          {adminContact.phone}
                        </a>
                      )}
                      {adminContact.website && (
                        <a 
                          href={adminContact.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 text-muted-foreground hover:text-white transition-colors"
                        >
                          <Globe className="w-5 h-5" />
                          {adminContact.website}
                        </a>
                      )}
                      
                      {/* Social Links */}
                      {adminContact.social_links && (
                        <div className="flex gap-4 pt-4">
                          {adminContact.social_links.instagram && (
                            <a
                              href={adminContact.social_links.instagram}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-10 h-10 border border-white/20 rounded-sm flex items-center justify-center text-muted-foreground hover:text-white hover:border-white/40 transition-colors"
                            >
                              <Instagram className="w-5 h-5" />
                            </a>
                          )}
                          {adminContact.social_links.facebook && (
                            <a
                              href={adminContact.social_links.facebook}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-10 h-10 border border-white/20 rounded-sm flex items-center justify-center text-muted-foreground hover:text-white hover:border-white/40 transition-colors"
                            >
                              <Facebook className="w-5 h-5" />
                            </a>
                          )}
                          {adminContact.social_links.twitter && (
                            <a
                              href={adminContact.social_links.twitter}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-10 h-10 border border-white/20 rounded-sm flex items-center justify-center text-muted-foreground hover:text-white hover:border-white/40 transition-colors"
                            >
                              <Twitter className="w-5 h-5" />
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {adminContact.bio && (
                    <div>
                      <p className="text-muted-foreground">{adminContact.bio}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Camera className="w-6 h-6 text-primary" />
                <span className="text-xl font-serif">360Lumina</span>
              </div>
              <p className="text-sm text-muted-foreground">
                The complete business platform for professional photographers.
              </p>
            </div>
            
            {/* Product */}
            <div>
              <h4 className="font-medium mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/features" className="hover:text-white transition-colors">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/galleries" className="hover:text-white transition-colors">Demo Galleries</Link></li>
              </ul>
            </div>
            
            {/* Resources */}
            <div>
              <h4 className="font-medium mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/help" className="hover:text-white transition-colors">Help Center</Link></li>
                <li><a href="/docs/photographer-guide.md" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Photographer Guide</a></li>
              </ul>
            </div>
            
            {/* Account */}
            <div>
              <h4 className="font-medium mb-4">Account</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/login" className="hover:text-white transition-colors">Login</Link></li>
                <li><Link to="/dashboard" className="hover:text-white transition-colors">Dashboard</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} 360Lumina. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <a href="https://blog.360lumina.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Blog</a>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/dmca" className="hover:text-white transition-colors">DMCA</Link>
              <a href="mailto:support@360lumina.com" className="hover:text-white transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
